import { useMemo } from "react";
import type { ComponentType } from "react";
import { Button, Flex, Heading, Text, View } from "@aws-amplify/ui-react";
import {
  createAmplifyAuthAdapter,
  createStorageBrowser,
  defaultActionConfigs,
  defaultHandlers,
} from "@aws-amplify/ui-react-storage/browser";
import type {
  ActionHandler,
  ActionViewConfig,
  StorageBrowserDisplayText,
} from "@aws-amplify/ui-react-storage/browser";

import { createFileListingView, formatLastModified } from "./listing/files";
import { LISTING_PAGE_SIZE } from "./listing/pagination";
import { createFullPageListHandler } from "./listing/fullPageListing";
import { ToolbarIconButton } from "./listing/toolbar";
import { createFolderListingView } from "./listing/folders";
import { createProjectSelection } from "./listing/projectSelection";
import type { UploadPolicy } from "./upload/validation";
import { createUploadPolicy } from "./upload/validation";
import { createUploadDisplayText } from "./upload/messages";
import { createUploadProgressView } from "./upload/progress";
import type { CustomerScope } from "@/auth/customerScope";
import { AppRole } from "@/auth/roles";
import { approveByMoving, rejectByDeletion } from "./approval";
import { isCandidateSelection } from "./qualityReports";
import { createS3Client } from "./s3Client";

const hiddenCreateFolder = {
  ...defaultActionConfigs.createFolder,
  actionListItem: {
    ...defaultActionConfigs.createFolder.actionListItem,
    hide: () => true,
  },
};

const hiddenCopy = {
  ...defaultActionConfigs.copy,
  actionListItem: {
    ...defaultActionConfigs.copy.actionListItem,
    hide: () => true,
  },
};

const defaultActionOverrides = {
  copy: hiddenCopy,
  createFolder: hiddenCreateFolder,
  listLocationItems: createFullPageListHandler(
    defaultHandlers.listLocationItems,
  ),
};

const evidoComponents = {
  DataRefresh: ({
    onRefresh,
    isDisabled,
  }: {
    readonly onRefresh?: () => void;
    readonly isDisabled?: boolean;
  }) => (
    <ToolbarIconButton
      iconName="refresh"
      isDisabled={isDisabled}
      label="Refresh"
      onSelect={() => onRefresh?.()}
    />
  ),
};

const LISTING_ERROR_MESSAGE = "There was an error loading items.";
const SEARCH_TRUNCATED_MESSAGE =
  "Showing results for up to the first 10,000 items.";

const evidoDisplayText = (
  uploadPolicy: UploadPolicy,
): StorageBrowserDisplayText => ({
  ...createUploadDisplayText(uploadPolicy),
  LocationDetailView: {
    getDateDisplayValue: formatLastModified,
    searchPlaceholder: "Search current folder",

    getListItemsResultMessage: ({
      hasError,
      hasExhaustedSearch,
      isLoading,
      items,
      message,
    }) => {
      if (isLoading) {
        return undefined;
      }
      if (hasError) {
        return { content: message ?? LISTING_ERROR_MESSAGE, type: "error" };
      }
      if (!items?.length) {
        return undefined;
      }
      return hasExhaustedSearch
        ? { content: SEARCH_TRUNCATED_MESSAGE, type: "info" }
        : undefined;
    },
  },
});

/** Leave an action view, clearing the row selection `onActionExit` leaves behind. */
function exitToFolder(detailState: {
  readonly onActionExit: () => void;
  readonly onRefresh: () => void;
}): void {
  detailState.onRefresh();
  detailState.onActionExit();
}

type ApproveData = { key: string; id: string };
type ApproveHandlerType = ActionHandler<ApproveData, { key: string }>;
type RejectData = { key: string; id: string };
type RejectHandlerType = ActionHandler<RejectData, { key: string }>;

const approveHandler: ApproveHandlerType = (input) => {
  const { config, data } = input;
  const result = (async () => {
    try {
      const client = await createS3Client(config.region);
      const destinationKey = await approveByMoving(
        client,
        config.bucket,
        data.key,
      );
      return { status: "COMPLETE" as const, value: { key: destinationKey } };
    } catch (error: unknown) {
      return {
        status: "FAILED" as const,
        message:
          error instanceof Error
            ? error.message
            : "Failed to approve the result.",
      };
    }
  })();
  return { result };
};

const rejectHandler: RejectHandlerType = (input) => {
  const { config, data } = input;
  const result = (async () => {
    try {
      const client = await createS3Client(config.region);
      const deletedKey = await rejectByDeletion(
        client,
        config.bucket,
        data.key,
      );
      return { status: "COMPLETE" as const, value: { key: deletedKey } };
    } catch (error: unknown) {
      return {
        status: "FAILED" as const,
        message:
          error instanceof Error
            ? error.message
            : "Failed to reject the result.",
      };
    }
  })();
  return { result };
};

function createQualityControlStorageBrowser(
  customerScope: CustomerScope | undefined,
  projectPrefixRoot: string,
): ComponentType {
  const approveAction: ActionViewConfig<ApproveHandlerType, "ApproveView"> = {
    handler: approveHandler,
    viewName: "ApproveView",
    actionListItem: {
      label: "Approve",
      // A results-document icon, distinct from the download action's arrow.
      icon: "file-text",
      disable: (selectedValues) => !isCandidateSelection(selectedValues),
    },
  };

  const rejectAction: ActionViewConfig<RejectHandlerType, "RejectView"> = {
    handler: rejectHandler,
    viewName: "RejectView",
    actionListItem: {
      label: "Reject",
      // A delete icon, signalling the destructive (non-reversible) nature of reject.
      icon: "delete-file",
      disable: (selectedValues) => !isCandidateSelection(selectedValues),
    },
  };

  const uploadPolicy = createUploadPolicy();
  const { StorageBrowser, useAction, useView } = createStorageBrowser({
    config: createAmplifyAuthAdapter(),
    actions: {
      default: defaultActionOverrides,
      custom: { approve: approveAction, reject: rejectAction },
    },
    components: evidoComponents,
    options: { validateFile: uploadPolicy.validateFile },
  });

  const ApproveView = () => {
    const detailState = useView("LocationDetail");
    const { dataItems } = detailState;
    // A stable `items` keeps the action's task sync from re-running every render.
    const items = useMemo(
      () =>
        (dataItems ?? [])
          .filter((item) => item.type === "FILE")
          .map((item) => ({ key: item.key, id: item.id })),
      [dataItems],
    );
    const [{ tasks, isProcessing, isProcessingComplete }, approve] = useAction(
      "approve",
      { items },
    );

    if (items.length === 0) {
      return (
        <Flex direction="column" gap="1rem" padding="1.5rem">
          <Heading level={2}>Approve results</Heading>
          <Text>Select one or more candidate files to approve.</Text>
          <Button
            alignSelf="flex-start"
            onClick={() => {
              exitToFolder(detailState);
            }}
          >
            Close
          </Button>
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap="1rem" padding="1.5rem">
        <Heading level={2}>Approve results</Heading>
        <Text>
          Approving moves these files out of <code>for_approval</code> into the
          project&apos;s <code>results</code> folder, releasing them to the
          customer. Each file&apos;s data quality report moves to the staff-only{" "}
          <code>evido_internal/approved_quality_reports</code> folder; a file
          without one is not approved:
        </Text>
        <View as="ul" margin="0">
          {tasks.length > 0
            ? tasks.map((task) => (
                <Text as="li" key={task.data.id}>
                  {task.data.key}
                  {task.status === "COMPLETE" ? " — approved" : null}
                  {task.status === "FAILED"
                    ? ` — failed: ${task.message ?? "unknown error"}`
                    : null}
                </Text>
              ))
            : items.map((item) => (
                <Text as="li" key={item.id}>
                  {item.key}
                </Text>
              ))}
        </View>
        <Flex gap="0.5rem">
          <Button
            onClick={() => approve()}
            isDisabled={isProcessing || isProcessingComplete}
            variation="primary"
          >
            {isProcessing
              ? "Approving…"
              : `Approve ${items.length} file${items.length === 1 ? "" : "s"}`}
          </Button>
          <Button
            onClick={() => {
              exitToFolder(detailState);
            }}
          >
            {isProcessingComplete ? "Done" : "Cancel"}
          </Button>
        </Flex>
      </Flex>
    );
  };

  const RejectView = () => {
    const detailState = useView("LocationDetail");
    const { dataItems } = detailState;
    // A stable `items` keeps the action's task sync from re-running every render.
    const items = useMemo(
      () =>
        (dataItems ?? [])
          .filter((item) => item.type === "FILE")
          .map((item) => ({ key: item.key, id: item.id })),
      [dataItems],
    );
    const [{ tasks, isProcessing, isProcessingComplete }, reject] = useAction(
      "reject",
      { items },
    );

    if (items.length === 0) {
      return (
        <Flex direction="column" gap="1rem" padding="1.5rem">
          <Heading level={2}>Reject results</Heading>
          <Text>Select one or more candidate files to reject.</Text>
          <Button
            alignSelf="flex-start"
            onClick={() => {
              exitToFolder(detailState);
            }}
          >
            Close
          </Button>
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap="1rem" padding="1.5rem">
        <Heading level={2}>Reject results</Heading>
        <Text>
          Rejecting permanently deletes these files, and their data quality
          reports, from <code>for_approval</code>. This cannot be undone:
        </Text>
        <View as="ul" margin="0">
          {tasks.length > 0
            ? tasks.map((task) => (
                <Text as="li" key={task.data.id}>
                  {task.data.key}
                  {task.status === "COMPLETE" ? " — rejected" : null}
                  {task.status === "FAILED"
                    ? ` — failed: ${task.message ?? "unknown error"}`
                    : null}
                </Text>
              ))
            : items.map((item) => (
                <Text as="li" key={item.id}>
                  {item.key}
                </Text>
              ))}
        </View>
        <Flex gap="0.5rem">
          <Button
            onClick={() => reject()}
            isDisabled={isProcessing || isProcessingComplete}
            variation="primary"
          >
            {isProcessing
              ? "Rejecting…"
              : `Reject ${items.length} file${items.length === 1 ? "" : "s"}`}
          </Button>
          <Button
            onClick={() => {
              exitToFolder(detailState);
            }}
          >
            {isProcessingComplete ? "Done" : "Cancel"}
          </Button>
        </Flex>
      </Flex>
    );
  };

  const LocationDetailView = createFileListingView(
    useView,
    StorageBrowser.LocationDetailView,
    AppRole.QualityControl,
    uploadPolicy.setRules,
    projectPrefixRoot,
  );

  const LocationsView = createFolderListingView(
    useView,
    StorageBrowser.LocationsView,
    customerScope,
    projectPrefixRoot,
  );

  const UploadView = createUploadProgressView(
    useView,
    StorageBrowser.UploadView,
    projectPrefixRoot,
  );

  const QualityControlStorageBrowser: ComponentType = () => (
    <StorageBrowser
      displayText={evidoDisplayText(uploadPolicy)}
      pageSize={LISTING_PAGE_SIZE}
      views={{
        ApproveView,
        LocationDetailView,
        LocationsView,
        RejectView,
        UploadView,
      }}
    />
  );
  return QualityControlStorageBrowser;
}

function createPlainStorageBrowser(
  role: AppRole,
  customerScope: CustomerScope | undefined,
  projectPrefixRoot: string,
): ComponentType {
  const uploadPolicy = createUploadPolicy();
  const { StorageBrowser, useView } = createStorageBrowser({
    config: createAmplifyAuthAdapter(),
    actions: { default: defaultActionOverrides },
    components: evidoComponents,
    options: { validateFile: uploadPolicy.validateFile },
  });

  const projectSelection =
    (customerScope?.projects.length ?? 0) > 1
      ? createProjectSelection()
      : undefined;

  const LocationDetailView = createFileListingView(
    useView,
    StorageBrowser.LocationDetailView,
    role,
    uploadPolicy.setRules,
    projectPrefixRoot,
    projectSelection,
  );

  const LocationsView = createFolderListingView(
    useView,
    StorageBrowser.LocationsView,
    customerScope,
    projectPrefixRoot,
    projectSelection,
  );

  const UploadView = createUploadProgressView(
    useView,
    StorageBrowser.UploadView,
    projectPrefixRoot,
  );

  const PlainStorageBrowser: ComponentType = () => (
    <StorageBrowser
      displayText={evidoDisplayText(uploadPolicy)}
      pageSize={LISTING_PAGE_SIZE}
      views={{ LocationDetailView, LocationsView, UploadView }}
    />
  );
  return PlainStorageBrowser;
}

export function createRoleStorageBrowser(
  role: AppRole,
  customerScope: CustomerScope | undefined,
  projectPrefixRoot: string,
): ComponentType {
  if (role === AppRole.QualityControl) {
    return createQualityControlStorageBrowser(customerScope, projectPrefixRoot);
  }
  return createPlainStorageBrowser(role, customerScope, projectPrefixRoot);
}
